import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  LayoutDashboard,
  AlertCircle,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { useUrlState } from "@/hooks/use-url-state";
import type { Deal } from "@shared/schema";

// ============================================================================
// CONSTANTS
// ============================================================================

type TransactionStage = "scenario" | "deal" | "funded_loan";

const TRANSACTION_STAGE_TABS: { value: TransactionStage; label: string }[] = [
  { value: "scenario", label: "Scenario" },
  { value: "deal", label: "Deal" },
  { value: "funded_loan", label: "Funded Loan" },
];

// Scenario Substages (5 exploration phases)
const SCENARIO_SUBSTAGES = [
  { value: "initial_inquiry", label: "Initial Inquiry", color: "bg-slate-500" },
  { value: "gathering_info", label: "Gathering Info", color: "bg-blue-500" },
  { value: "seeking_indicatives", label: "Seeking Indicatives", color: "bg-indigo-500" },
  { value: "preparing_proposal", label: "Preparing Proposal", color: "bg-purple-500" },
  { value: "awaiting_decision", label: "Awaiting Decision", color: "bg-violet-500" },
];

// Deal Substages (7 application phases)
const DEAL_SUBSTAGES = [
  { value: "application_prep", label: "Application Prep", color: "bg-amber-500" },
  { value: "submitted", label: "Submitted", color: "bg-yellow-500" },
  { value: "lender_assessment", label: "Lender Assessment", color: "bg-orange-500" },
  { value: "conditional_approval", label: "Conditional Approval", color: "bg-lime-500" },
  { value: "formal_approval", label: "Formal Approval", color: "bg-green-500" },
  { value: "settlement_booked", label: "Settlement Booked", color: "bg-cyan-500" },
  { value: "settled", label: "Settled", color: "bg-emerald-500" },
];

// Funded Loan (single stage)
const FUNDED_LOAN_SUBSTAGES = [
  { value: "funded_loan", label: "Funded Loan", color: "bg-emerald-600" },
];

function getSubstagesForStage(stage: TransactionStage) {
  switch (stage) {
    case "scenario":
      return SCENARIO_SUBSTAGES;
    case "deal":
      return DEAL_SUBSTAGES;
    case "funded_loan":
      return FUNDED_LOAN_SUBSTAGES;
  }
}

// ============================================================================
// TYPES
// ============================================================================

interface PipelineColumn {
  substage: string;
  label: string;
  dealCount: number;
  totalValue: number;
}

interface PipelineSummary {
  transactionStage: string;
  columns: PipelineColumn[];
  totalDeals: number;
  totalPipelineValue: number;
}

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "$0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}

function getDaysInStage(updatedAt: string | Date | null | undefined): number {
  if (!updatedAt) return 0;
  const updated = new Date(updatedAt);
  const now = new Date();
  return Math.floor((now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24));
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

function DealCard({ deal, onClick }: { deal: Deal; onClick: () => void }) {
  const days = getDaysInStage(deal.updatedAt);
  const initials = getInitials(deal.dealName);

  return (
    <Card
      data-testid={`deal-card-${deal.id}`}
      className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-primary/30"
      onClick={onClick}
    >
      <CardContent className="p-3 space-y-2">
        {/* Deal reference / name */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-tight truncate">
            {deal.dealName}
          </p>
          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-muted shrink-0">
            <User className="h-3 w-3 text-muted-foreground" />
          </div>
        </div>

        {/* Amount */}
        <p className="text-sm font-semibold text-primary">
          {formatCurrency(deal.amountRequested)}
        </p>

        {/* Bottom row: days in stage + broker initials */}
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="text-xs">
            {days}d
          </Badge>
          <span className="text-xs text-muted-foreground font-medium">
            {initials}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function PipelineColumnView({
  substage,
  label,
  color,
  deals,
  summaryData,
  onCardClick,
}: {
  substage: string;
  label: string;
  color: string;
  deals: Deal[];
  summaryData: PipelineColumn | undefined;
  onCardClick: (dealId: string) => void;
}) {
  const dealCount = summaryData?.dealCount ?? deals.length;
  const totalValue = summaryData?.totalValue ?? 0;

  return (
    <div
      data-testid={`pipeline-column-${substage}`}
      className="flex flex-col min-w-[240px] max-w-[300px] flex-1"
    >
      {/* Column Header */}
      <div className="rounded-t-lg bg-muted/50 border border-b-0 p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
          <span className="text-sm font-semibold truncate">{label}</span>
          <Badge variant="secondary" className="ml-auto text-xs">
            {dealCount}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatCompactCurrency(totalValue)}
        </p>
      </div>

      {/* Column Body */}
      <div className="flex-1 border rounded-b-lg bg-muted/20 p-2 space-y-2 min-h-[200px] overflow-y-auto max-h-[calc(100vh-300px)]">
        {deals.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[120px] text-muted-foreground text-sm">
            No deals at this stage
          </div>
        ) : (
          deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              onClick={() => onCardClick(deal.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function SkeletonColumn() {
  return (
    <div className="flex flex-col min-w-[240px] max-w-[300px] flex-1">
      <div className="rounded-t-lg bg-muted/50 border border-b-0 p-3">
        <Skeleton className="h-4 w-24 mb-1" />
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="flex-1 border rounded-b-lg bg-muted/20 p-2 space-y-2 min-h-[200px]">
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PipelineBoardPage() {
  const [, setLocation] = useLocation();
  const [activeStage, setActiveStage] = useUrlState<TransactionStage>(
    "stage",
    "deal"
  );

  // Fetch deals filtered by transaction stage
  const {
    data: allDeals = [],
    isLoading: dealsLoading,
    isError: dealsError,
  } = useQuery<Deal[]>({
    queryKey: [
      "/api/crm/deals",
      { transactionStage: activeStage },
    ],
  });

  // Fetch pipeline summary
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery<PipelineSummary>({
    queryKey: [
      "/api/crm/deals/pipeline-summary",
      { transactionStage: activeStage },
    ],
  });

  const substages = getSubstagesForStage(activeStage);
  const isLoading = dealsLoading || summaryLoading;
  const isError = dealsError || summaryError;

  // Group deals by their substage
  const dealsBySubstage = new Map<string, Deal[]>();
  for (const substage of substages) {
    dealsBySubstage.set(substage.value, []);
  }
  for (const deal of allDeals) {
    const substageKey =
      activeStage === "scenario"
        ? deal.scenarioSubstage
        : activeStage === "deal"
          ? deal.dealSubstage
          : "funded_loan";
    if (substageKey && dealsBySubstage.has(substageKey)) {
      dealsBySubstage.get(substageKey)!.push(deal);
    }
  }

  // Sort deals within each column by updatedAt descending
  Array.from(dealsBySubstage.values()).forEach((columnDeals) => {
    columnDeals.sort(
      (a: Deal, b: Deal) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  });

  const handleCardClick = (dealId: string) => {
    setLocation(`/deals/${dealId}`);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Pipeline Board"
        breadcrumbs={[
          { label: "Deals", href: "/deals" },
          { label: "Pipeline Board" },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation("/deals")}
          >
            <LayoutDashboard className="h-4 w-4 mr-2" />
            List View
          </Button>
        }
      />

      <div className="flex-1 overflow-hidden px-4">
        {/* Hidden element for test identification */}
        <div data-testid="pipeline-board-title" className="sr-only">
          Pipeline Board
        </div>

        {/* Transaction Stage Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b pb-2">
          {TRANSACTION_STAGE_TABS.map((tab) => (
            <Button
              key={tab.value}
              data-testid={`stage-tab-${tab.value}`}
              variant={activeStage === tab.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveStage(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Pipeline Summary Bar */}
        {summary && !isLoading && (
          <div
            data-testid="pipeline-summary"
            className="flex items-center gap-6 mb-4 rounded-lg bg-muted/50 border p-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Pipeline:</span>
              <span className="text-sm font-semibold">
                {summary.totalDeals} {summary.totalDeals === 1 ? "deal" : "deals"}
              </span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">
                {formatCurrency(summary.totalPipelineValue)}
              </span>
              <span className="text-sm text-muted-foreground">total</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="flex items-center gap-2 text-destructive p-4">
            <AlertCircle className="h-5 w-5" />
            <span>Failed to load pipeline data. Please try again.</span>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {substages.map((s) => (
              <SkeletonColumn key={s.value} />
            ))}
          </div>
        )}

        {/* Pipeline Columns */}
        {!isLoading && !isError && (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {substages.map((substage) => {
              const columnSummary = summary?.columns.find(
                (c) => c.substage === substage.value
              );
              return (
                <PipelineColumnView
                  key={substage.value}
                  substage={substage.value}
                  label={substage.label}
                  color={substage.color}
                  deals={dealsBySubstage.get(substage.value) || []}
                  summaryData={columnSummary}
                  onCardClick={handleCardClick}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
